import React, { useState } from 'react';
import { Box, Container, Typography, TextField, Button, Link as MuiLink, Divider, IconButton, Alert, Snackbar } from '@mui/material';
import Grid from '@mui/material/Grid';
import { Link } from 'react-router-dom';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { submitForm } from '../utils/formSubmission';

const footerLinks = {
  Services: [
    { label: 'Email Deliverability', to: '/services/email-deliverability' },
    { label: 'Email Auditing', to: '/services/email-audit' },
    { label: 'Email Marketing', to: '/services/email-marketing' },
    { label: 'Search Engine Optimisation', to: '/services/seo' },
    { label: 'Search Engine Auditing', to: '/services/seo-audit' },
    { label: 'Content & Blog Writing', to: '/services/content-writing' },
    { label: 'LinkedIn Marketing', to: '/services/linkedin-marketing' },
    { label: 'LinkedIn Profile Optimization', to: '/services/linkedin-optimization' },
  ],
  Products: [
    { label: 'IQDMARC', to: '/products/iqdmarc' },
    { label: 'SPAM0', to: '/products/spam0' },
  ],
  Tools: [
    { label: 'DNS Super Tool', to: '/tools/dns-super-tool' },
    { label: 'SPF Analyzer', to: '/tools/spf-analyzer' },
    { label: 'DMARC Analyzer', to: '/tools/dmarc-analyzer' },
    { label: 'DKIM Analyzer', to: '/tools/dkim-analyzer' },
    { label: 'MX Analyzer', to: '/tools/mx-analyzer' },
  ],
  Company: [
    { label: 'About Us', to: '/about' },
    { label: 'Careers', to: '/careers' },
    { label: 'Contact', to: '/contact' },
    { label: 'Blog', to: '/blog' },
  ],
};

const socialLinks = [
  { icon: <FacebookIcon fontSize="small" />, href: 'https://facebook.com' },
  { icon: <TwitterIcon fontSize="small" />, href: 'https://twitter.com' },
  { icon: <LinkedInIcon fontSize="small" />, href: 'https://linkedin.com' },
  { icon: <YouTubeIcon fontSize="small" />, href: 'https://youtube.com' },
];

const Footer: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter your email address',
        severity: 'error'
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid email address',
        severity: 'error'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = {
        email: email,
        page_name: 'footer',
        request_for: 'Newsletter Subscription',
        source: 'footer_newsletter',
        timestamp: new Date().toISOString()
      };

      await submitForm(formData, {
        formName: 'newsletter_subscription',
        formSource: 'footer',
        onSuccess: () => {
          setSnackbar({
            open: true,
            message: 'Thank you for subscribing to our newsletter!',
            severity: 'success'
          });
          setEmail(''); // Clear the form
        },
        onError: (error) => {
          console.error('Newsletter subscription error:', error);
          setSnackbar({
            open: true,
            message: 'Failed to subscribe. Please try again.',
            severity: 'error'
          });
        },
        onLoading: (loading) => {
          setIsSubmitting(loading);
        }
      });
      
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setSnackbar({
        open: true,
        message: 'Failed to subscribe. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ background: '#1158A6', color: 'white', pt: 4, pb: 0, mt: 8 }} component="footer">
      <Container maxWidth="lg">
        <Grid container spacing={2} sx={{ pb: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', width: '100%', gap: { xs: 2, md: 0 }, justifyContent: 'space-between' }}>
            <Box sx={{ minWidth: 120, flex: '1 1 120px', mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>Services</Typography>
              {footerLinks.Services.slice(0, 4).map(link => (
                <MuiLink component={Link} to={link.to} key={link.to} color="inherit" underline="hover" sx={{ display: 'block', mb: 0.5, fontSize: '0.85rem', opacity: 0.85 }}>
                  {link.label}
                </MuiLink>
              ))}
            </Box>
            <Box sx={{ minWidth: 100, flex: '1 1 100px', mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>Products</Typography>
              {footerLinks.Products.map(link => (
                <MuiLink component={Link} to={link.to} key={link.to} color="inherit" underline="hover" sx={{ display: 'block', mb: 0.5, fontSize: '0.85rem', opacity: 0.85 }}>
                  {link.label}
                </MuiLink>
              ))}
            </Box>
            <Box sx={{ minWidth: 120, flex: '1 1 120px', mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>Tools</Typography>
              {footerLinks.Tools.map(link => (
                <MuiLink component={Link} to={link.to} key={link.to} color="inherit" underline="hover" sx={{ display: 'block', mb: 0.5, fontSize: '0.85rem', opacity: 0.85 }}>
                  {link.label}
                </MuiLink>
              ))}
            </Box>
            <Box sx={{ minWidth: 100, flex: '1 1 100px', mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>Company</Typography>
              {footerLinks.Company.map(link => (
                <MuiLink component={Link} to={link.to} key={link.to} color="inherit" underline="hover" sx={{ display: 'block', mb: 0.5, fontSize: '0.85rem', opacity: 0.85 }}>
                  {link.label}
                </MuiLink>
              ))}
            </Box>
            <Box sx={{ minWidth: 220, flex: '2 1 220px', mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>Newsletter</Typography>
              <Typography variant="caption" sx={{ opacity: 0.8, mb: 1, display: 'block' }}>
                Get the latest deliverability tips, product updates, and more.
              </Typography>
              <Box component="form" onSubmit={handleSubmit} noValidate autoComplete="off" sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <TextField
                  fullWidth
                  variant="filled"
                  size="small"
                  placeholder="Your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                  InputProps={{ 
                    disableUnderline: true, 
                    sx: { 
                      borderRadius: 2, 
                      background: 'white', 
                      color: '#1158A6', 
                      px: 1, 
                      fontSize: '0.9rem',
                      '&.Mui-focused': {
                        background: 'white',
                      },
                      '&:hover': {
                        background: 'white',
                      }
                    } 
                  }}
                />
                <Button 
                  type="submit"
                  variant="contained" 
                  disabled={isSubmitting}
                  sx={{ 
                    borderRadius: 2, 
                    px: 2, 
                    fontSize: '0.85rem', 
                    minWidth: 0,
                    opacity: isSubmitting ? 0.7 : 1,
                    backgroundColor: 'white',
                    color: '#1158A6',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    },
                    '&:disabled': {
                      backgroundColor: 'rgba(255, 255, 255, 0.5)',
                      color: '#1158A6',
                    }
                  }}
                >
                  {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                </Button>
              </Box>
              <Typography variant="caption" sx={{ opacity: 0.6, mt: 1, display: 'block' }}>
                We respect your privacy.
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.15)' }} />
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', justifyContent: 'space-between', py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, sm: 0 } }}>
            <img src="/images/impactquill_svg_white.svg" alt="ImpactQuill Logo" style={{ height: 28, width: 'auto', marginRight: 12, filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.15))' }} />
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              ImpactQuill is a leading provider of email deliverability, marketing, and security solutions.
            </Typography>
          </Box>
          <Box>
            {socialLinks.map((s, i) => (
              <IconButton key={i} href={s.href} target="_blank" rel="noopener" sx={{ color: 'white', mr: 0.5, p: 0.5 }}>
                {s.icon}
              </IconButton>
            ))}
          </Box>
        </Box>
        <Box textAlign="center" sx={{ opacity: 0.7, pb: 1 }}>
          <Typography variant="caption">
            © {new Date().getFullYear()} ImpactQuill. All rights reserved.
          </Typography>
        </Box>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Footer; 